import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import type { PartnerProgramMembership } from "../../shared/types";
import { useNative } from "../../shared/phone/media";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { PARTNER_PORTAL } from "../../shared/config";
import { PortalMark } from "../components/Logo";
import { PortalModeToggle } from "../components/PortalModeToggle";
import { NativeFrame } from "../components/phone/Frame";
import { useOpenProgram } from "../components/phone/parts";
import { ProgramsNative } from "./ProgramsNative";

function letter(name: string): string {
  return (name.trim()[0] ?? "P").toUpperCase();
}

function programPill(p: PartnerProgramMembership) {
  if (p.needsResign) return <span className="pill hold">Re-sign</span>;
  if (p.agreementSigned) return <span className="pill ok">Active</span>;
  return <span className="pill hold">Sign to start</span>;
}

/**
 * The programs landing: every program the partner belongs to. Each opens into the
 * app when it is set up, or into the onboarding wizard when it still needs the
 * agreement (or a re-sign). Real membership data from GET /partner/me.
 */
export function ProgramsView() {
  const { session, logout } = usePartnerAuth();
  const navigate = useNavigate();
  const native = useNative();
  const open = useOpenProgram();
  const programs = session?.programs ?? [];
  const only = programs.length === 1 ? programs[0] : null;

  // Why: on a phone a list of one is a step with nothing to choose, so the one program opens straight away.
  const opened = useRef(false);
  useEffect(() => {
    if (!native || !only || opened.current) return;
    opened.current = true;
    open(only, true);
  }, [native, only, open]);

  if (native) {
    return only ? null : (
      <NativeFrame nav={false}>
        <ProgramsNative fromAccount={false} />
      </NativeFrame>
    );
  }

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="programs">
      <div className="prog-topright">
        <PortalModeToggle />
        <button type="button" className="prog-logout" onClick={onLogout}>
          Log out
        </button>
      </div>
      <div className="prog-wrap">
        <div className="brand">
          <PortalMark />
          Partner <span className="l">Programs</span>
        </div>
        <h1>Your programs</h1>
        <p className="sub">
          Pick a product to promote. We walk you through setup the first time you open one.
        </p>

        {programs.length === 0 ? (
          <div className="es">
            <div className="es-ic" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="16" rx="2.5" />
                <path d="M3 9h18" />
              </svg>
            </div>
            <div className="es-eyebrow">No programs yet</div>
            <h3>Nothing to promote yet</h3>
            <p>Programs you are invited to will appear here. Ask a program owner for an invite link.</p>
          </div>
        ) : (
          <div className="prog-list">
            {programs.map((p) => (
              <button key={p.programId} type="button" className="prog-card" onClick={() => open(p)}>
                <span className="plogo" aria-hidden="true">
                  {letter(p.productName)}
                </span>
                <div className="pinfo">
                  <div className="pn">{p.productName}</div>
                  <div className="pd">
                    {p.status === "pending" ? "Pending review" : "Partner program"}
                  </div>
                </div>
                {programPill(p)}
              </button>
            ))}
          </div>
        )}

        <div className="prog-powered">{PARTNER_PORTAL.poweredBy}</div>
      </div>
    </div>
  );
}
